import { AssistantPart } from '@services/modules/AssistantStream'
import assistantStore from '@stores/assistantStore'
import { ComponentType } from 'react'

import { openAssistantRoute } from '../assistantNavigation'
import { HandoffLink, MetaRow } from '../ui'

import ConfirmationPart from './ConfirmationPart'

/*
 * Renderers for the structured parts the server attaches to an answer. A new part type is one
 * component plus one registry entry; unknown types render nothing, so an older client stays
 * safe when the server learns a new part.
 */

interface PartProps {
  part: AssistantPart
}

/** "Open in list view": the classic screen pre-filtered with what the assistant searched. */
const HandoffPart = ({ part }: PartProps) => {
  if (!part.route) {
    return null
  }
  return (
    <HandoffLink onClick={() => openAssistantRoute(part.route, part.query)}>
      {part.label || 'Open in list view'}
    </HandoffLink>
  )
}

/** "Open the … guide": the page of the user guide the answer came from. */
const GuidePart = ({ part }: PartProps) => {
  if (!part.docKey) {
    return null
  }
  return (
    <HandoffLink onClick={() => assistantStore.openGuide(part.docKey)}>
      {part.label || 'Open the guide'}
    </HandoffLink>
  )
}

/** Inline parts share one footer row; block parts (cards) take the full width. */
const INLINE_PARTS: Record<string, ComponentType<PartProps>> = {
  handoff: HandoffPart,
  guide: GuidePart,
}

const BLOCK_PARTS: Record<string, ComponentType<PartProps>> = {
  confirmation: ConfirmationPart,
}

const MessageParts = ({ parts }: { parts?: AssistantPart[] }) => {
  const blocks = (parts ?? []).filter(p => BLOCK_PARTS[p.type])
  // Two searches of one answer can hand off to the same list with the same filters: one link.
  const seen = new Set<string>()
  const inline = (parts ?? []).filter(p => {
    if (!INLINE_PARTS[p.type]) {
      return false
    }
    const key = JSON.stringify([
      p.type,
      p.label,
      p.route,
      Object.entries(p.query ?? {}).sort(([a], [b]) => a.localeCompare(b)),
      p.docKey,
    ])
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })

  return (
    <>
      {blocks.map((part, index) => {
        const Renderer = BLOCK_PARTS[part.type]
        return (
          <Renderer
            key={part.actionId ?? `${part.type}-${index}`}
            part={part}
          />
        )
      })}
      {inline.length > 0 && (
        <MetaRow>
          {inline.map((part, index) => {
            const Renderer = INLINE_PARTS[part.type]
            return <Renderer key={`${part.type}-${index}`} part={part} />
          })}
        </MetaRow>
      )}
    </>
  )
}

export default MessageParts
